// Port of the Nohaya Career app's render_model.dart: a template-independent,
// print-ready view of a resume that every template renders from. Built from
// a builder resume (src/lib/resumeBuilder/renderModel.ts) or, here, from one
// sample resume row (job_title_resumes) plus a placeholder identity, the way
// the app's "use this sample" flow fills a resume.

export type ContactKind = "email" | "phone" | "location" | "website" | "linkedin" | "github" | "link" | "birth" | "nationality";

export type ContactItem = { kind: ContactKind; text: string; url?: string };

export type RenderSectionType =
  | "summary"
  | "experience"
  | "education"
  | "skills"
  | "projects"
  | "certifications"
  | "languages"
  | "entries"
  | "bullets"
  | "tags"
  | "references";

export type RichLine = { text: string; bullet: boolean };

export type RenderEntry = {
  title: string;
  subtitle: string;
  meta: string;
  dates: string;
  url?: string;
  lines: RichLine[];
  tags: string[];
};

export type RenderSkill = { name: string; level: number; levelLabel: string; category: string };

export type RenderSection = {
  key: string;
  type: RenderSectionType;
  title: string;
  entries: RenderEntry[];
  skills: RenderSkill[];
  lines: RichLine[];
  tags: string[];
  /** Section-heading icon id (sectionIcons.ts); drawn only when section icons are on. */
  iconId?: string;
};

export type RenderResume = {
  name: string;
  headline: string;
  contacts: ContactItem[];
  sections: RenderSection[];
  /** Square photo (data URL or URL), when the resume has one and the design shows it. */
  photo?: string | null;
};

const SIDEBAR_FRIENDLY = new Set<RenderSectionType>(["skills", "languages", "tags", "certifications", "references"]);

/** Short, list-like sections that suit a narrow sidebar column. */
export function sidebarFriendly(s: RenderSection): boolean {
  return SIDEBAR_FRIENDLY.has(s.type);
}

/** Skills grouped by category, in first-appearance order. */
export function skillGroups(s: RenderSection): [string, RenderSkill[]][] {
  const groups = new Map<string, RenderSkill[]>();
  for (const skill of s.skills) {
    const list = groups.get(skill.category) ?? [];
    list.push(skill);
    groups.set(skill.category, list);
  }
  return [...groups.entries()];
}

export function hasCategories(s: RenderSection): boolean {
  return s.skills.some((x) => x.category.length > 0);
}

export function entryHasHeader(e: RenderEntry): boolean {
  return e.title.length > 0 || e.subtitle.length > 0 || e.meta.length > 0 || e.dates.length > 0;
}

export function firstName(m: RenderResume): string {
  const parts = m.name.trim().split(/\s+/);
  return parts.length > 1 ? parts.slice(0, -1).join(" ") : m.name;
}

export function lastName(m: RenderResume): string {
  const parts = m.name.trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

export function initials(m: RenderResume): string {
  return m.name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

// ---------------------------------------------------------------------------

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, code: string) => {
    if (code[0] === "#") {
      const n = code[1].toLowerCase() === "x" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : m;
    }
    return ENTITIES[code.toLowerCase()] ?? m;
  });
}

/** Removes control characters and emoji and normalises whitespace. */
export function clean(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu, "")
    .replace(/\t/g, " ")
    .replace(/[  ]{2,}/g, " ")
    .trim();
}

function stripTags(html: string): string {
  return clean(decodeEntities(html.replace(/<[^>]+>/g, " ")));
}

/**
 * `<ul><li>a</li><li>b</li></ul>` → ["a", "b"]; plain text falls back to
 * sentences (the app's `_htmlToBullets`).
 */
export function htmlToBullets(html: string | null | undefined): string[] {
  if (!html) return [];
  const items = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((m) => stripTags(m[1])).filter(Boolean);
  if (items.length) return items;
  return stripTags(html)
    .split(".")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function splitCommaList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return stripTags(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * "Bachelor of Fine Arts in Animation, University of Arts, 2012" →
 * degree, institution and (when the last part is a year) the year.
 */
export function parseEducation(raw: string | null | undefined): { degree: string; institution: string; year: string } {
  const parts = stripTags(raw ?? "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return { degree: "", institution: "", year: "" };
  const last = parts[parts.length - 1];
  const year = parts.length > 1 && /^(19|20)\d{2}$/.test(last) ? last : "";
  const rest = year ? parts.slice(0, -1) : parts;
  return { degree: rest[0] ?? "", institution: rest.slice(1).join(", "), year };
}

// ---------------------------------------------------------------------------

/** One sample resume as returned by GET /api/resumes/{slug}. */
export type ResumeSample = {
  id: number;
  resume_variant_no: number;
  resume_title: string;
  professional_summary: string;
  experience_title_1: string | null;
  experience_company_1: string | null;
  experience_description_1: string | null;
  experience_title_2: string | null;
  experience_company_2: string | null;
  experience_description_2: string | null;
  education: string | null;
  skills: string | null;
  achievements: string | null;
  total_years_experience: string | number | null;
};

/** Fields a preview needs — keeps client props small. */
export function pickSample(r: ResumeSample): ResumeSample {
  return {
    id: r.id,
    resume_variant_no: r.resume_variant_no,
    resume_title: r.resume_title,
    professional_summary: r.professional_summary,
    experience_title_1: r.experience_title_1,
    experience_company_1: r.experience_company_1,
    experience_description_1: r.experience_description_1,
    experience_title_2: r.experience_title_2,
    experience_company_2: r.experience_company_2,
    experience_description_2: r.experience_description_2,
    education: r.education,
    skills: r.skills,
    achievements: r.achievements,
    total_years_experience: r.total_years_experience,
  };
}

/** The placeholder identity the old sample renderer used on every resume. */
export const SAMPLE_PERSON = {
  name: "Michael Anderson",
  email: "michael.anderson@email.com",
  phone: "(555) 234-5678",
  location: "San Francisco, CA",
  linkedin: "linkedin.com/in/michaelanderson",
};

const SAMPLE_LANGUAGES: RenderSkill[] = [
  { name: "English", level: 1, levelLabel: "Native", category: "" },
  { name: "Spanish", level: 0.75, levelLabel: "Fluent", category: "" },
  { name: "French", level: 0.5, levelLabel: "Conversational", category: "" },
];

function section(key: string, type: RenderSectionType, title: string, parts: Partial<RenderSection>): RenderSection {
  return { key, type, title, entries: [], skills: [], lines: [], tags: [], ...parts };
}

function entry(parts: Partial<RenderEntry>): RenderEntry {
  return { title: "", subtitle: "", meta: "", dates: "", lines: [], tags: [], ...parts };
}

/**
 * Builds the print model for one sample. Dates are spread over the sample's
 * own years of experience, ending at [year] (the current year).
 */
export function buildSampleModel(sample: ResumeSample, year: number): RenderResume {
  const title1 = clean(sample.experience_title_1);
  const title2 = clean(sample.experience_title_2);
  const totalYears = Math.round(Number(sample.total_years_experience) || 0);
  const years = Math.min(30, Math.max(2, totalYears || 4));
  const hasSecond = title2.length > 0;
  const firstStart = year - (hasSecond ? Math.max(1, Math.round(years * 0.5)) : years);
  const secondStart = year - years;

  const experience: RenderEntry[] = [];
  if (title1) {
    experience.push(
      entry({
        title: title1,
        subtitle: clean(sample.experience_company_1),
        dates: `${firstStart} – Present`,
        lines: htmlToBullets(sample.experience_description_1).map((text) => ({ text, bullet: true })),
      })
    );
  }
  if (hasSecond) {
    experience.push(
      entry({
        title: title2,
        subtitle: clean(sample.experience_company_2),
        dates: secondStart < firstStart ? `${secondStart} – ${firstStart}` : `${secondStart}`,
        lines: htmlToBullets(sample.experience_description_2).map((text) => ({ text, bullet: true })),
      })
    );
  }

  const edu = parseEducation(sample.education);
  const skills = splitCommaList(sample.skills);
  const achievements = htmlToBullets(sample.achievements);
  const summary = clean(decodeEntities(sample.professional_summary ?? ""));

  const sections: RenderSection[] = [];
  if (summary) sections.push(section("summary", "summary", "Profile", { lines: [{ text: summary, bullet: false }] }));
  if (experience.length) sections.push(section("experience", "experience", "Experience", { entries: experience }));
  if (edu.degree || edu.institution) {
    sections.push(
      section("education", "education", "Education", {
        entries: [entry({ title: edu.degree, subtitle: edu.institution, dates: edu.year })],
      })
    );
  }
  if (skills.length) {
    sections.push(
      section("skills", "skills", "Skills", {
        skills: skills.map((name) => ({ name, level: 0.75, levelLabel: "Advanced", category: "" })),
      })
    );
  }
  sections.push(section("languages", "languages", "Languages", { skills: SAMPLE_LANGUAGES }));
  if (achievements.length) {
    sections.push(
      section("achievements", "bullets", "Achievements", {
        lines: achievements.map((text) => ({ text, bullet: true })),
      })
    );
  }

  return {
    name: SAMPLE_PERSON.name,
    headline: title1 || clean(sample.resume_title),
    contacts: [
      { kind: "email", text: SAMPLE_PERSON.email },
      { kind: "phone", text: SAMPLE_PERSON.phone },
      { kind: "location", text: SAMPLE_PERSON.location },
      { kind: "linkedin", text: SAMPLE_PERSON.linkedin },
    ],
    sections,
  };
}
